import { Button, LoadingButton } from '@eventbrite/design-ops-ds';
import { Icon } from '@eventbrite/eds-icon';
import { BoltFill } from '@eventbrite/eds-iconography';
import { Link } from '@eventbrite/eds-link';
import { gettext } from '@eventbrite/i18n';
import cx from 'classnames';
import React, { useState } from 'react';
import { useInterval } from 'react-use';
import { TimelineWithBenefits } from '..';
import { BlobSVG } from '../components/BlobSVG';
import { Illustrations } from '../components/Illustrations';
import { Timeline } from '../components/Timeline';
import '../styles/boostBenefits.scss';
import { BenefitsItem, BoostBenefitsProps } from '../types';

export const TimelineResponsive: React.FC<{
    statsContent?: React.ReactElement;
    items: Array<BenefitsItem>;
    staticParam?: boolean;
    activeIdx: number;
}> = ({ statsContent, items, staticParam, activeIdx }) => {
    return (
        <div className="boost-benefits__benefits-block-container">
            {statsContent}
            <div>
                <div className="time-line">
                    <Timeline
                        items={items}
                        activeIdx={activeIdx}
                        static={staticParam}
                    />
                </div>
            </div>
        </div>
    );
};

export const BoostBenefitsBlockWithStats: React.FC<{
    statsContent?: React.ReactElement;
    items: Array<BenefitsItem>;
    staticParam?: boolean;
    activeIdx: number;
    intervalSpeedInMS: number | null;
}> = ({ statsContent, items, staticParam, intervalSpeedInMS }) => {
    return (
        <div className="boost-benefits__benefits-block-container">
            {statsContent}
            <div className="boost-benefits__benefits-block-with-content">
                <TimelineWithBenefits
                    items={items}
                    static={staticParam}
                    intervalSpeedInMS={intervalSpeedInMS}
                />
            </div>
        </div>
    );
};
export const BoostBenefits: React.FC<BoostBenefitsProps> = ({
    CTAButtonText = gettext('Upgrade to Boost'),
    declineButtonText = gettext('No thanks'),
    ...props
}) => {
    const [activeIdx, setActiveIdx] = useState<number>(0);

    const intervalSpeed = props.static ? null : props.intervalSpeedMS || 3000;

    useInterval(() => {
        setActiveIdx((prevIdx) =>
            prevIdx < props.items.length - 1 ? prevIdx + 1 : 0,
        );
    }, intervalSpeed);

    let ctaButton;

    if (props.isTrialFlow) {
        ctaButton = (
            <LoadingButton
                variant="primary"
                onClick={props.onCTAButtonClick}
                id="boost-benefits-primary-cta-button"
                isLoading={!!props.isActivatingTrial}
            >
                {CTAButtonText}
            </LoadingButton>
        );
    } else {
        ctaButton = (
            <Button
                variant="primary"
                onClick={props.onCTAButtonClick}
                id="boost-benefits-primary-cta-button"
            >
                {CTAButtonText}
            </Button>
        );
    }

    const boostBenefitsBlock = (
        <div className="boost-benefits__benefits-block">
            <TimelineWithBenefits
                items={props.items}
                static={props.static}
                intervalSpeedInMS={intervalSpeed}
            />
        </div>
    );

    return (
        <div className={cx(['boost-benefits', { static: props.static }])}>
            <div className="boost-benefits__content">
                <div className="boost-benefits__content--left">
                    <Icon type={<BoltFill />} size="medium" />
                    <h1 className="heading">{props.headingContent}</h1>
                    {props.statsContent ? (
                        <BoostBenefitsBlockWithStats
                            activeIdx={activeIdx}
                            staticParam={props.static}
                            items={props.items}
                            statsContent={props.statsContent}
                            intervalSpeedInMS={intervalSpeed}
                        />
                    ) : (
                        boostBenefitsBlock
                    )}
                </div>
                <div className="boost-benefits__content--right">
                    <BlobSVG />
                    <Illustrations
                        items={props.items}
                        activeIdx={activeIdx}
                        staticIllustration={props.staticIllustration}
                    />
                </div>
            </div>

            <div className="boost-benefits__bottom-bar">
                {props.bottomBarContent}

                <div className="boost-benefits__bottom-bar--buttons">
                    <Button
                        variant="ghost"
                        onClick={props.onDeclineButtonClick}
                        id="boost-benefits-decline-button"
                    >
                        {declineButtonText}
                    </Button>
                    {props.bottomBarCTALink ? (
                        <Link to={props.bottomBarCTALink}>{ctaButton}</Link>
                    ) : (
                        ctaButton
                    )}
                </div>
            </div>
        </div>
    );
};
